<template>
    <div
        class="text-slate-800 relative max-w-full sm:min-w-[480px]"
        :class="{
            'sm:overflow-y-auto scroll-thin sm:max-h-[calc(100dvh-96px)]': !props.hideScroll,
        }"
    >
        <div
            ref="headerRef"
            class="top-0 z-10 bg-white px-4 pt-4 lg:px-6 lg:pt-6"
            :class="props.hideScroll ? 'sticky' : 'sm:sticky'"
        >
            <div class="flex justify-between items-center gap-2 mb-4">
                <h2 class="text-lg font-extrabold m-0">Group Switcher</h2>
                <div v-if="onCorporate() && !props.hideLinks" class="flex gap-2">
                    <vx-button
                        :href="`/settings/teams/${teamId}#/groups`"
                        color="muted"
                        size="xs"
                        data-test="groups-link"
                    >
                        Groups
                        <font-awesome-icon widthAuto :icon="faArrowUpRight"></font-awesome-icon>
                    </vx-button>
                    <vx-button
                        :href="`/settings/teams/${teamId}#/org-units`"
                        color="muted"
                        size="xs"
                        data-test="orgs-link"
                    >
                        OUs
                        <font-awesome-icon widthAuto :icon="faArrowUpRight"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>

            <div class="mb-4 bg-slate-100 rounded-2xl p-2">
                <vx-input
                    size="md"
                    v-model="search"
                    maxlength="40"
                    ref="searchInputRef"
                    placeholder="Search"
                    block
                    :aria-activedescendant="
                        toggledOptions[activeIndex]
                            ? 'context_switch_' + contextKey(toggledOptions[activeIndex])
                            : undefined
                    "
                    @keydown="onListKeyDown"
                >
                    <template v-slot:right>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
                <!-- The hidden label sizes the select to its selected option. -->
                <div class="mt-1 relative inline-block">
                    <span class="block invisible whitespace-nowrap text-xs h-8 leading-8 ps-4 pe-8" aria-hidden="true">
                        {{ statusFilterLabel }}
                    </span>
                    <div class="absolute inset-0">
                        <vx-select
                            v-model="statusFilter"
                            size="sm"
                            class="shadow-none! bg-transparent! text-xs! pe-8! cursor-pointer hover:bg-slate-200! focus:bg-slate-200! focus-visible:ring-0! focus-visible:ring-offset-0!"
                            data-test="status-filter"
                            :options="statusOptions"
                        ></vx-select>
                    </div>
                </div>
            </div>

            <component
                :is="onCorporate() ? 'div' : 'span'"
                v-if="(canAccessCorporate || availableOrgUnit) && !search.length"
                ref="corporateRef"
                data-test="top-option"
                @click="canAccessCorporate ? switchToCorporate() : switchOption(availableOrgUnit)"
                @keydown.enter="canAccessCorporate ? switchToCorporate() : switchOption(availableOrgUnit)"
                @focus="activeIndex = -1"
                tabindex="0"
                :aria-selected="activeIndex === -1"
                class="text-slate-700! hover:no-underline hover:bg-sky-100 focus:bg-sky-100 flex justify-between items-center py-2.5 px-3 rounded-lg cursor-pointer outline-hidden"
                :class="{
                    'bg-sky-100': activeIndex === -1,
                }"
            >
                <template v-if="canAccessCorporate">
                    <div class="flex gap-2 items-center flex-row">
                        <vx-radio
                            :modelValue="(onCorporate() && selectedIndex == undefined) || selectedIndex === -1"
                        ></vx-radio>
                        <span class="text-xs font-bold">View as Corporate</span>
                    </div>
                    <vx-badge color="amber" size="xs">
                        Corporate
                        <font-awesome-icon widthAuto :icon="faCrown" class="text-amber-500"></font-awesome-icon>
                    </vx-badge>
                </template>
                <template v-else>
                    <div class="flex gap-2 items-center flex-row">
                        <vx-radio
                            :modelValue="currentOrgUnit?.id === availableOrgUnit.id || selectedIndex === -1"
                        ></vx-radio>
                        <span class="text-xs font-bold">View as {{ availableOrgUnit.name }}</span>
                    </div>
                    <vx-badge color="violet" size="xs">
                        Org
                        <font-awesome-icon widthAuto :icon="faBuildingFlag" class="text-violet-500"></font-awesome-icon>
                    </vx-badge>
                </template>
            </component>
            <hr class="border-slate-200 -mx-4 lg:-mx-6 my-4" />
        </div>

        <div
            role="listbox"
            class="flex flex-col px-4 pb-4 lg:px-6 lg:pb-6"
            :class="{
                'gap-2': search.length,
            }"
        >
            <div v-if="loading" class="text-slate-500 text-center text-sm">Loading...</div>
            <div v-else-if="error && !contexts.length" class="text-rose-600 text-center text-sm" data-test="error">
                Could not load groups and org units.
            </div>
            <div v-else-if="!contexts.length" class="text-slate-500 text-center text-sm">
                No groups or org units yet.
            </div>
            <div v-else-if="!toggledOptions.length && search" class="text-slate-500 text-center text-sm">
                No results found with "{{ search }}"
            </div>
            <div
                v-else-if="!toggledOptions.length && statusFilter !== 'all'"
                class="text-slate-500 text-center text-sm"
            >
                No {{ statusFilter }} groups found.
            </div>
            <component
                :is="isCurrentOption(toggledOption) ? 'div' : 'span'"
                v-for="(toggledOption, index) in toggledOptions"
                :id="'context_switch_' + contextKey(toggledOption)"
                :key="contextKey(toggledOption)"
                role="option"
                :data-test="`option-${contextKey(toggledOption)}`"
                :aria-selected="index === activeIndex ? true : false"
                tabindex="0"
                :ref="(el) => (optionsRef[index] = el)"
                @click="switchOption(toggledOption)"
                @keydown.enter="switchOption(toggledOption)"
                @focus="activeIndex = index"
                @mouseenter="
                    () => {
                        if (disableMouseIndexChange) {
                            return;
                        }
                        activeIndex = index;
                    }
                "
                @mousemove="disableMouseIndexChange = false"
                class="text-slate-700! hover:no-underline flex items-center justify-between gap-2 rounded-sm py-2 px-3 cursor-pointer outline-hidden"
                :class="{
                    'bg-sky-100': activeIndex === index,
                    'border border-solid border-slate-100': search.length,
                }"
            >
                <div class="flex flex-1 items-center gap-2 flex-row min-w-0">
                    <vx-radio
                        :modelValue="
                            (isCurrentOption(toggledOption) && selectedIndex == undefined) || selectedIndex === index
                        "
                    ></vx-radio>
                    <div class="flex-1 min-w-0">
                        <div class="text-xs text-slate-700! font-bold line-clamp-1" data-test="name">
                            <highlighter
                                :searchWords="searchHighlight"
                                :autoEscape="true"
                                highlightClassName="text-amber-600 p-0 bg-transparent"
                                :textToHighlight="toggledOption.name"
                            >
                            </highlighter>
                        </div>
                        <div
                            v-if="toggledOption.type === 'group'"
                            class="text-xs leading-tight text-slate-500! truncate w-0 min-w-full"
                            data-test="group-id"
                        >
                            <highlighter
                                :searchWords="searchHighlight"
                                :autoEscape="true"
                                :caseSensitive="true"
                                highlightClassName="text-amber-600 p-0 bg-transparent"
                                :textToHighlight="toggledOption.id"
                            >
                            </highlighter>
                        </div>
                        <div
                            v-if="toggledOption.type === 'group'"
                            class="leading-tight"
                            :class="{ '-mt-1': showsPhoneNumber(toggledOption) }"
                        >
                            <vx-popover
                                v-if="showsPhoneNumber(toggledOption)"
                                hover
                                arrow
                                placement="top"
                                class="[&>div]:inline-block"
                                data-test="phone-info"
                            >
                                <div class="text-xs text-slate-500! flex items-center gap-1">
                                    <span
                                        :class="{ 'line-through': phoneNumberStruck(toggledOption) }"
                                        data-test="phone-number"
                                    >
                                        {{ phoneNumberLabel(toggledOption) }}
                                    </span>
                                    <font-awesome-icon
                                        v-if="sendsFromCorporateNumber(toggledOption)"
                                        widthAuto
                                        :icon="faCrown"
                                        data-test="corporate-number"
                                    ></font-awesome-icon>
                                </div>
                                <template v-slot:content>
                                    <div class="text-center max-w-[200px] text-sm">
                                        <div v-for="line in phoneNumberExplanation(toggledOption)" :key="line">
                                            {{ line }}
                                        </div>
                                    </div>
                                </template>
                            </vx-popover>
                            <div
                                v-if="toggledOption.group_status !== 'active'"
                                class="text-xs capitalize"
                                :class="statusClasses[toggledOption.group_status]"
                                data-test="status"
                            >
                                {{ toggledOption.group_status }}
                            </div>
                        </div>
                    </div>
                </div>
                <vx-badge v-if="toggledOption.type === 'org_unit'" color="violet" size="xs" class="shrink-0">
                    Org
                    <font-awesome-icon widthAuto :icon="faBuildingFlag" class="text-violet-500"></font-awesome-icon>
                </vx-badge>
                <vx-badge v-if="toggledOption.type === 'group'" color="slate" size="xs" class="shrink-0">
                    Group
                    <font-awesome-icon widthAuto :icon="faGrid2Plus" class="text-slate-500"></font-awesome-icon>
                </vx-badge>
            </component>
            <div v-if="filteredOptions.length > PER_PAGE" class="pt-4">
                <vx-button role="option" color="muted" block @click="toggleOptions" tabindex="0" data-test="load-more">
                    {{ showAll ? 'Show Less' : 'Load More' }}
                </vx-button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { VxRadio, VxButton, VxInput, VxBadge, VxPopover, VxSelect } from '@voxie/frontend-components';
import {
    faArrowUpRight,
    faBuildingFlag,
    faCrown,
    faGrid2Plus,
    faMagnifyingGlass,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import Highlighter from 'vue-highlight-words';
import { formatPhoneNumber } from '~/components/filters';
import { GROUP_STATUS_ACTIVE } from '~/constants/groupStatuses';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useContextSwitch } from '~/composables/useContextSwitch';
import { useToasted } from '~/composables/useToasted';
import { contextsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import axios from 'axios';

const PER_PAGE = 8;
const CORPORATE_INDEX = -1;

const emit = defineEmits(['switching']);
const props = defineProps({
    hideScroll: Boolean,
    hideLinks: Boolean,
});

const teamId = Spark.state.currentTeam.id;

const { recents, getContextId, switchContext } = useContextSwitch();
const initialRecents = [...recents.value];
const { onCorporate, currentGroup, currentOrgUnit, canAccessCorporate } = useCurrentGroup();
const toasted = useToasted();

const contextKey = (option) => getContextId(option.type === 'group' ? 'group' : 'orgUnit', option.id);
const cachedContexts = useLocalStorage(`voxie_contexts_${teamId}_${Spark.state.user.id}`, null, {
    serializer: StorageSerializers.object,
});
const refreshing = ref(false);
const error = ref(false);
const contexts = computed(() => cachedContexts.value ?? []);
const loading = computed(() => refreshing.value && cachedContexts.value === null);

const refresh = async () => {
    refreshing.value = true;
    error.value = false;
    try {
        const response = await contextsClient.getContexts(teamId);
        cachedContexts.value = response.data.data;
    } catch {
        error.value = true;
    } finally {
        refreshing.value = false;
    }
};

const statusOptions = [
    { value: 'active', label: 'Filter: Active' },
    { value: 'draft', label: 'Filter: Draft' },
    { value: 'suspended', label: 'Filter: Suspended' },
    { value: 'all', label: 'Filter: All' },
];
const statusClasses = {
    draft: 'text-amber-600',
    suspended: 'text-rose-600',
    closed: 'text-slate-500',
};

const searchInputRef = ref();
const search = ref('');
const statusFilter = ref('active');
const statusFilterLabel = computed(() => statusOptions.find((option) => option.value === statusFilter.value)?.label);
const showAll = ref(false);
const searchHighlight = computed(() => [search.value]);

onMounted(() => {
    searchInputRef.value?.field?.focus();
    refresh();
});

const orgUnits = computed(() => contexts.value.filter((option) => option.type === 'org_unit'));
const availableOrgUnit = computed(() => (orgUnits.value.length === 1 ? orgUnits.value[0] : null));
const groupsOnly = computed(() => availableOrgUnit.value && !canAccessCorporate.value);

const isCurrentOption = (option) => {
    if (option.type === 'group') {
        return currentGroup.value?.group_id === option.id;
    }

    return currentOrgUnit.value?.id === option.id;
};

const compareByRecency = (a, b) => {
    if (isCurrentOption(a) !== isCurrentOption(b)) {
        return isCurrentOption(a) ? -1 : 1;
    }

    const aRecency = initialRecents.indexOf(contextKey(a));
    const bRecency = initialRecents.indexOf(contextKey(b));

    if (aRecency === -1 && bRecency === -1) {
        return a.name.localeCompare(b.name);
    }

    if (aRecency === -1 || bRecency === -1) {
        return aRecency === -1 ? 1 : -1;
    }

    return aRecency - bRecency;
};

const matchesStatus = (option) => {
    if (isCurrentOption(option)) {
        return true;
    }

    if (option.type !== 'group') {
        return ['active', 'all'].includes(statusFilter.value);
    }

    return statusFilter.value === 'all' || option.group_status === statusFilter.value;
};

const matchesSearch = (option) => {
    if (!search.value.length) {
        return true;
    }

    return option.name.toLowerCase().includes(search.value.toLowerCase()) || String(option.id).includes(search.value);
};

const filteredOptions = computed(() =>
    contexts.value
        .filter((option) => !groupsOnly.value || option.type === 'group')
        .filter((option) => search.value.length > 0 || matchesStatus(option))
        .filter(matchesSearch)
        .sort(compareByRecency)
);

const toggledOptions = computed(() =>
    showAll.value ? filteredOptions.value : filteredOptions.value.slice(0, PER_PAGE)
);

const toggleOptions = async () => {
    showAll.value = !showAll.value;
    await nextTick();
    searchInputRef.value?.field?.focus();

    if (activeIndex.value > toggledOptions.value.length - 1) {
        activeIndex.value = getInitialIndex();
    }
};

const sendsFromGroupNumber = (option) =>
    !option.group_uses_corporate_number && option.group_phone_number_used_for_sending;

const sendsFromCorporateNumber = (option) =>
    option.group_uses_corporate_number && Boolean(option.corporate_phone_number);

const displayedPhoneNumber = (option) =>
    option.group_phone_number ?? (option.group_uses_corporate_number ? option.corporate_phone_number : null);

const groupsStartActive = Spark.state.currentTeam.default_group_status === GROUP_STATUS_ACTIVE;

const showsPhoneNumber = (option) =>
    !groupsStartActive || option.group_uses_corporate_number || Boolean(displayedPhoneNumber(option));

const phoneNumberLabel = (option) => {
    const phoneNumber = displayedPhoneNumber(option);

    return phoneNumber ? formatPhoneNumber(phoneNumber) : 'No number assigned';
};

const phoneNumberStruck = (option) => Boolean(option.group_phone_number) && !sendsFromGroupNumber(option);

const phoneNumberExplanation = (option) => {
    if (sendsFromCorporateNumber(option)) {
        return option.group_phone_number
            ? [`Corporate number ${formatPhoneNumber(option.corporate_phone_number)} used for sending.`]
            : ['Corporate number used for sending.'];
    }

    if (option.group_uses_corporate_number) {
        return ['No corporate number to send from.', 'This group cannot send messages.'];
    }

    if (option.group_phone_number_used_for_sending) {
        return ['Group number used for sending.'];
    }

    if (option.group_phone_number) {
        return ['Number assigned but not enabled for sending.', 'This group cannot send messages.'];
    }

    return ['No number assigned.', 'This group cannot send messages.'];
};

const switchingOption = ref(false);

const handleContextSwitch = (request) => {
    request
        .then(() => {
            if (window.location.pathname.startsWith('/message-hub')) {
                window.location.replace('/message-hub');
            } else {
                window.location.reload();
            }
        })
        .catch((error) => {
            toasted.error(catchRequestError(error));
        })
        .finally(() => {
            switchingOption.value = false;
        });
};

const switchOption = async (toggledOption) => {
    if (switchingOption.value) {
        return;
    }

    if (!isCurrentOption(toggledOption)) {
        switchingOption.value = true;
        emit('switching');
        selectedIndex.value = activeIndex.value;

        const type = toggledOption.type === 'group' ? 'group' : 'orgUnit';
        handleContextSwitch(switchContext(type, toggledOption.id));
    }
};

const switchToCorporate = async () => {
    emit('switching');
    selectedIndex.value = CORPORATE_INDEX;
    handleContextSwitch(axios.get(`/settings/teams/${teamId}/switch`));
};

const getInitialIndex = () => (onCorporate() ? 0 : canAccessCorporate.value ? CORPORATE_INDEX : 0);
const activeIndex = ref(getInitialIndex());

const selectedIndex = ref();
const disableMouseIndexChange = ref(false);

const getNextIndex = (current, direction) => {
    const initialIndex = getInitialIndex();
    const max = toggledOptions.value.length - 1;
    if (direction === 'down') {
        if (current < max) {
            return current + 1;
        }
        if (current === max) {
            return initialIndex;
        }
        if (current === CORPORATE_INDEX) {
            return 0;
        }
    } else if (direction === 'up') {
        if (current > 0) {
            return current - 1;
        }
        if (current === 0) {
            return initialIndex;
        }
        if (current === CORPORATE_INDEX) {
            return max;
        }
    }
    return current;
};

const onListKeyDown = (event) => {
    if (!toggledOptions.value.length && !canAccessCorporate.value) {
        return;
    }

    if (event.key === 'ArrowDown') {
        disableMouseIndexChange.value = true;
        event.preventDefault();
        if (
            activeIndex.value === toggledOptions.value.length - 1 &&
            filteredOptions.value.length > PER_PAGE &&
            !showAll.value
        ) {
            showAll.value = true;
        }

        activeIndex.value = getNextIndex(activeIndex.value, 'down');
        scrollToActive();
    } else if (event.key === 'ArrowUp') {
        disableMouseIndexChange.value = true;
        event.preventDefault();
        activeIndex.value = getNextIndex(activeIndex.value, 'up');
        scrollToActive();
    } else if (event.key === 'Enter') {
        event.preventDefault();
        if (activeIndex.value === CORPORATE_INDEX && canAccessCorporate.value && !search.value.length) {
            switchToCorporate();
        } else if (activeIndex.value >= 0 && toggledOptions.value[activeIndex.value]) {
            switchOption(toggledOptions.value[activeIndex.value]);
        }
    }
};

const corporateRef = ref();
const headerRef = ref();
const optionsRef = ref([]);
const isElementInViewport = (el, offset = 50) => {
    const rect = el.getBoundingClientRect();
    const top = headerRef.value?.getBoundingClientRect().bottom ?? offset;

    return rect.top >= top && rect.bottom <= window.innerHeight - offset;
};

const scrollToActive = async () => {
    await nextTick();

    if (!optionsRef.value) {
        return;
    }

    const el = activeIndex.value === CORPORATE_INDEX ? corporateRef.value : optionsRef.value[activeIndex.value];
    if (el && el?.scrollIntoView && !isElementInViewport(el)) {
        el.scrollIntoView({ block: 'center' });
    }
};

watch([search, statusFilter], async () => {
    showAll.value = false;
    activeIndex.value = search.value.length ? 0 : getInitialIndex();
    await nextTick();
    scrollToActive();
});

watch(contexts, () => {
    if (activeIndex.value > toggledOptions.value.length - 1) {
        activeIndex.value = getInitialIndex();
    }
});
</script>
